import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "next-themes";
import { Web3Provider } from '@/components/Web3Provider';
import { SolanaWalletProvider } from '@/components/SolanaWalletProvider';

// Import pages
import Index from '@/pages/Index';
import Initiation from '@/pages/Initiation';
import MiniApp from '@/pages/MiniApp';
import Brotocol from '@/pages/Brotocol';
import NotFound from '@/pages/NotFound';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

function App() {
  console.log('App.tsx: Full application component rendering...');
  console.log('App.tsx: Build process working correctly');
  
  // Initialize button gatekeeping system on app load
  useEffect(() => {
    console.log('App.tsx: Full application useEffect running...');
    
    // Hide the HTML fallback loading screen
    if (typeof window !== 'undefined' && (window as any).hideFallback) {
      console.log('App.tsx: Hiding fallback loading screen');
      (window as any).hideFallback();
    }
    
    try {
      // Import and initialize gatekeeping only if available
      import("./lib/buttonGatekeeping").then(module => {
        console.log('App.tsx: Button gatekeeping loaded');
        if (module.initializeGatekeeping) {
          module.initializeGatekeeping();
        }
      }).catch(error => {
        console.warn('App.tsx: Button gatekeeping not available:', error);
      });
    } catch (error) {
      console.warn('App.tsx: Error loading button gatekeeping:', error);
    }
    
    console.log('App.tsx: Full application initialization complete');
  }, []);

  console.log('App.tsx: About to render full application JSX...');
  
  try {
    return (
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
        <QueryClientProvider client={queryClient}>
          <Web3Provider>
            <SolanaWalletProvider>
              <Router>
                <div className="min-h-screen bg-background text-foreground">
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/initiation" element={<Initiation />} />
                    <Route path="/mini-app" element={<MiniApp />} />
                    <Route path="/brotocol" element={<Brotocol />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  <Toaster />
                </div>
              </Router>
            </SolanaWalletProvider>
          </Web3Provider>
        </QueryClientProvider>
      </ThemeProvider>
    );
  } catch (error) {
    console.error('App.tsx: Error in full application render:', error);
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: '#19152a', 
        color: '#e7d4cc',
        padding: '20px',
        fontFamily: 'Arial, sans-serif'
      }}>
        <h1>Application Error</h1>
        <p>Error: {error instanceof Error ? error.message : String(error)}</p>
        <button 
          onClick={() => window.location.reload()}
          style={{
            background: '#e7d4cc',
            color: '#19152a',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '5px',
            cursor: 'pointer',
            marginTop: '1rem',
            fontSize: '16px'
          }}
        >
          Reload Application
        </button>
      </div>
    );
  }
}

export default App;
